import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2Request,
  apiV2AssertResponse,
  hasBusinessMembership,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

type ReadAction =
  | { kind: "conversation"; messageId: number }
  | { kind: "all"; snapshotAt: Date };

export const useMessageMarkRead = () => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const queryClient = useQueryClient();
  const canMarkRead = hasBusinessMembership({ user, business });
  const mutation = useMutation({
    onMutate: () => ({ uid: user?.uid, businessId: business?.id }),
    mutationFn: async (action: ReadAction) => {
      if (!canMarkRead) {
        throw new Error("Business membership required.");
      }
      const response = await apiV2Request({
        user,
        business,
        method: "post",
        path:
          action.kind === "all"
            ? "messages/mark-all-read"
            : "messages/mark-read",
        data:
          action.kind === "all"
            ? { snapshotAt: action.snapshotAt }
            : { messageId: action.messageId },
      });
      return apiV2AssertResponse<{ success: true }>(response).data;
    },
    onSuccess: (_data, _action, context) => {
      for (const key of [
        ServerStateKeys.MessageInbox,
        ServerStateKeys.MessageUnread,
      ]) {
        queryClient.invalidateQueries({
          queryKey: [context?.uid, context?.businessId, key],
        });
      }
    },
  });
  return { ...mutation, canMarkRead };
};
